import React, { useEffect, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";

import {
  ActionLink,
  CurrencyFormatters,
  TravelWalletSelectionWorkflow,
  filterOutSpendAndGetOffers,
  getRestrictedATCText,
  getRestrictedUserTCTexts,
  isSpendAndGetOffer,
  getCreditActivityGroups,
  formatPartialHistoryValidFromDate,
  getUpcomingCreditActivityItems,
} from "halifax";
import { CaponeEmbeddedProvider, CreditActivityDialog } from "@capone/portal";
import "@capone/portal/style.css";
import { CreditActivityDialog as VesperCreditActivityDialog } from "@vesper/portal";
import { CreditActivityDialog as CorpCreditActivityDialog } from "@capone-corporate/portal";
import { getConfigTenant, isCorpTenant, isVesperTenant } from "@capone/common";

import { ClientContext } from "../../../../App";
import { TravelWalletSelectionConnectorProps } from "./container";
import {
  AVAILABLE,
  C1_AUTO_SELECT_CARD_AT_CHECKOUT,
  getExperimentVariant,
  useExperiments,
} from "../../../../context/experiments";
import {
  APPLY_TRAVEL_CREDIT_TITLE,
  APPLY_TRAVEL_OFFER_TITLE,
  CREDIT_BALANCE_DESCRIPTION,
  CREDIT_HISTORY_BALANCE_TEXT,
  CREDIT_HISTORY_MODAL_LIST_HEADER,
  CREDIT_HISTORY_MODAL_SUBHEADER,
  TRAVEL_CREDIT_AND_OFFER_TOTAL_PAYMENT_TEXT,
  TRAVEL_CREDIT_SUBTITLE,
  TRAVEL_CREDIT_TOTAL_PAYMENT_TEXT,
  TRAVEL_OFFER_SUBTITLE,
  TRAVEL_OFFER_TOTAL_PAYMENT_TEXT,
  VIEW_CREDIT_HISTORY_CTA,
  getSelectedCreditDescription,
  getSelectedOfferDescription,
} from "./textConstants";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { ENGAGED_OFFER_CTA, SelectedTravelOfferScreen, Tenant } from "redmond";

const DO_NOT_APPLY_REWARDS_KEY = "do-not-apply-rewards";

export interface ITravelWalletSelectionProps
  extends TravelWalletSelectionConnectorProps,
    RouteComponentProps {
  className?: string;
  isMobile?: boolean;
  disabled?: boolean;
  isAnnualTravelCreditsExperiment?: boolean;
  isCreditAndOfferStackingExperimentV1?: boolean;
  isTravelSale?: boolean;
}

const getCreditActivityDialogByTenant = (tenant: Tenant) => {
  if (isCorpTenant(tenant)) return CorpCreditActivityDialog;
  if (isVesperTenant(tenant)) return VesperCreditActivityDialog;
  return CreditActivityDialog;
};

export const TravelWalletSelection = (props: ITravelWalletSelectionProps) => {
  const {
    offers,
    offerToApply,
    setOfferToApply,
    credits,
    creditBreakdown,
    creditsToApply,
    setCreditToApply,
    disabled,
    isTravelCreditPaymentOnly,
    isTravelWalletOfferPaymentOnly,
    isStackedTravelWalletPaymentOnly,
    isMobile,
    creditsHistoryTransaction,
    partialHistoryValidFromTime,
    selectedPaymentMethodId,
    selectedRewardsPaymentAccountId,
    setSelectedRewardsPaymentTotal,
    resetPaymentCardSelectedAccounts,
    bestOverallOffer,
    rewardsAccounts,
    isAnnualTravelCreditsExperiment,
    isCreditAndOfferStackingExperimentV1,
    maxApplicableTravelWalletCredit,
    isTravelSale,
    ineligibleCreditsBreakdown,
  } = props;

  const firstName =
    React.useContext(ClientContext).sessionInfo?.userInfo?.firstName || "";

  // Filter out spend and get offers
  const filteredOffers = useMemo(
    () => (offers ? filterOutSpendAndGetOffers(offers) : undefined),
    [offers],
  );

  const filteredBestOverallOffer = useMemo(
    () =>
      bestOverallOffer && isSpendAndGetOffer(bestOverallOffer.descriptions)
        ? undefined
        : bestOverallOffer,
    [bestOverallOffer],
  );

  if (!filteredOffers && !credits) return null;

  const tenant = getConfigTenant();
  const ResolvedCreditActivityDialog = getCreditActivityDialogByTenant(tenant);
  const isCapone = !isCorpTenant(tenant) && !isVesperTenant(tenant);

  const expState = useExperiments();
  const autoSelectExperimentOn =
    getExperimentVariant(
      expState.experiments,
      C1_AUTO_SELECT_CARD_AT_CHECKOUT,
    ) === AVAILABLE;

  useEffect(() => {
    if (credits) {
      setCreditToApply(credits);
    }
  }, [credits]);

  useEffect(() => {
    const hasRealRewardsSelection =
      selectedRewardsPaymentAccountId &&
      selectedRewardsPaymentAccountId !== DO_NOT_APPLY_REWARDS_KEY;

    if (selectedPaymentMethodId || hasRealRewardsSelection) {
      if (!autoSelectExperimentOn) {
        resetPaymentCardSelectedAccounts();
      }
      setSelectedRewardsPaymentTotal("", null, null, null);
    }
  }, [offerToApply, creditsToApply, autoSelectExperimentOn]);

  useEffect(() => {
    if (filteredBestOverallOffer) {
      setOfferToApply(filteredBestOverallOffer);
    }
  }, [filteredBestOverallOffer]);

  const creditActivityDialog = creditsHistoryTransaction?.length ? (
    <ResolvedCreditActivityDialog
      trigger={<ActionLink content={VIEW_CREDIT_HISTORY_CTA} />}
      groups={getCreditActivityGroups(creditsHistoryTransaction, creditBreakdown)}
      upcomingItems={getUpcomingCreditActivityItems(creditBreakdown)}
      partialHistoryValidFromDate={formatPartialHistoryValidFromDate(
        partialHistoryValidFromTime,
      )}
    />
  ) : undefined;

  return (
    <TravelWalletSelectionWorkflow
      isMobile={isMobile}
      offersAvailable={filteredOffers || []}
      creditsAvailable={credits}
      offerToApply={offerToApply}
      creditsToApply={creditsToApply}
      setOfferToApply={setOfferToApply}
      setCreditToApply={setCreditToApply}
      titles={{
        creditTitle: APPLY_TRAVEL_CREDIT_TITLE,
        creditSubtitle: TRAVEL_CREDIT_SUBTITLE,
        offersTitle: APPLY_TRAVEL_OFFER_TITLE,
        offersSubtitle: TRAVEL_OFFER_SUBTITLE,
      }}
      getCreditDescription={getSelectedCreditDescription}
      getOfferDescription={getSelectedOfferDescription}
      disabled={disabled}
      totalCoverageTexts={{
        creditsCoverTotalText: isTravelCreditPaymentOnly
          ? TRAVEL_CREDIT_TOTAL_PAYMENT_TEXT
          : undefined,
        offerCoversTotalText: isTravelWalletOfferPaymentOnly
          ? TRAVEL_OFFER_TOTAL_PAYMENT_TEXT
          : undefined,
        travelWalletCoverTotalText: isStackedTravelWalletPaymentOnly
          ? TRAVEL_CREDIT_AND_OFFER_TOTAL_PAYMENT_TEXT
          : undefined,
      }}
      isTravelWalletCreditPaymentOnly={isTravelCreditPaymentOnly}
      isTravelWalletOfferPaymentOnly={isTravelWalletOfferPaymentOnly}
      maxApplicableCreditAmount={maxApplicableTravelWalletCredit}
      travelCreditHistoryProps={
        creditsHistoryTransaction?.length
          ? {
              modalContentProps: {
                header: CREDIT_BALANCE_DESCRIPTION(firstName),
                balanceText: CREDIT_HISTORY_BALANCE_TEXT(
                  CurrencyFormatters.getSymbol(credits?.amount.currency),
                  CurrencyFormatters.get(credits?.amount.currency).format(
                    (credits?.amount?.amount && credits?.amount?.amount * -1) ||
                      0,
                  ),
                ),
                subHeader: CREDIT_HISTORY_MODAL_SUBHEADER,
                creditListHeader: CREDIT_HISTORY_MODAL_LIST_HEADER,
              },
              viewHistoryCTAText: VIEW_CREDIT_HISTORY_CTA,
              creditTransactionsHistory: creditsHistoryTransaction,
              credit: credits,
              creditBreakdown,
              rewardsAccounts,
            }
          : undefined
      }
      creditActivityDialogSlot={
        creditActivityDialog && isCapone ? (
          <CaponeEmbeddedProvider>{creditActivityDialog}</CaponeEmbeddedProvider>
        ) : (
          creditActivityDialog
        )
      }
      isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
      isCreditAndOfferStackingExperimentV1={
        isCreditAndOfferStackingExperimentV1
      }
      restrictedATCText={
        getRestrictedATCText(rewardsAccounts, creditBreakdown) ||
        getRestrictedUserTCTexts(rewardsAccounts, creditBreakdown)?.[0]
      }
      isTravelSale={isTravelSale}
      onClickOfferTooltip={(offer) => {
        trackEvent({
          eventName: ENGAGED_OFFER_CTA,
          properties: {
            location: SelectedTravelOfferScreen.PREMIUM_STAYS_CHECKOUT,
            entry_type: "tooltip",
            funnel: offer.funnels.join(","),
          },
          encryptedProperties: [
            offer.trackingPropertiesV2?.encryptedProperties ?? "",
          ],
        });
      }}
      ineligibleCreditsBreakdown={ineligibleCreditsBreakdown}
    />
  );
};
